/**
 * Admin: fundraiser / seasonal campaigns — stable public link + checkout deep link.
 * Uses portal login session (adminFetch) — same auth as Contact messages Send reply.
 *
 * Tammy-focused UX:
 * - List: status, product, sold, revenue (price × sold), health chip, one-click Active toggle, QR code button.
 * - Modal: visibility, go-live checklist, basics (with one-click product fix), schedule,
 *   share tools (email + UTM links), organizer, banner image, and copy helpers.
 *   Product photo on the public page still comes from the Menu item.
 */

import { useEffect, useState } from 'react';
import { HiPlus, HiPencil, HiTrash, HiExternalLink, HiClipboardCopy, HiMail, HiLink } from 'react-icons/hi';
import { HiQrCode } from 'react-icons/hi2';
import ImageSelector from '../components/ImageSelector';
import { adminFetch, adminHeaders } from '../lib/adminApi';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface MenuItemRow {
  id: string;
  name: string;
  price: number;
  category: string;
  image_url?: string | null;
  /** From GET /api/menu — warn if false so checkout works on the fundraiser link. */
  is_available?: boolean;
  /** From GET /api/menu — must be true for fundraiser checkout button to appear. */
  is_orderable_online?: boolean;
}

interface CampaignRow {
  id: string;
  slug: string;
  title: string;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  /** When false, public page hides the "Fundraiser" line + big title (poster-style). */
  show_landing_heading?: boolean | null;
  hero_text: string | null;
  thank_you_text: string | null;
  hero_image_url?: string | null;
  /** Name of the group/school running the fundraiser. */
  organizer_name?: string | null;
  /** Free-form contact line (phone, email, etc.). */
  organizer_contact?: string | null;
  menu_item_id: string;
  max_units: number | null;
  sold_units: number;
}

const emptyForm = {
  title: '',
  slug: '',
  is_active: true,
  starts_at: '',
  ends_at: '',
  /** Tammy can rely on a poster image for wording and turn off the visible headline row. */
  show_landing_heading: true,
  hero_text: '',
  thank_you_text: '',
  hero_image_url: '',
  organizer_name: '',
  organizer_contact: '',
  menu_item_id: '',
  max_units: '',
};

function api(path: string) {
  return API_BASE ? `${API_BASE}${path}` : path;
}

function generateSlug(name: string) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Starter copy Tammy can insert then edit (placeholders in [brackets]). */
const COPY_SNIPPETS = {
  schoolFundraiser: {
    hero: `Every cake ordered helps [School or team name] reach their goal. Cakes are baked fresh at Nanny B's in Barrie. Pick a pickup date at checkout — thank you for supporting our community!`,
    thankYou: `Thank you for supporting [School or team name].\n\nQuestions about this fundraiser? Contact [Organizer name] at [phone or email].\n\nPickup is at Nanny B's Bakery unless your organizer tells you otherwise.`,
  },
  short: {
    hero: `Order by [date] for pickup the week of [event]. Share this page with friends and family — each order is separate.`,
    thankYou: `Pickup at Nanny B's Bakery in Barrie. You will get a confirmation email after you pay.`,
  },
} as const;

/** YYYY-MM-DD in local calendar for comparing date-only campaign windows. */
function localYyyyMmDd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIsoDateOnly(iso: string | null): string | null {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

/**
 * Human-readable row status. Paused = admin turned Active off. Scheduled / Ended = date window.
 */
function campaignRowStatus(c: CampaignRow): { label: string; className: string } {
  if (!c.is_active) {
    return { label: 'Paused', className: 'bg-gray-100 text-gray-800 border border-gray-200' };
  }
  const today = localYyyyMmDd(new Date());
  const start = parseIsoDateOnly(c.starts_at);
  const end   = parseIsoDateOnly(c.ends_at);
  if (start && today < start) {
    return { label: 'Scheduled', className: 'bg-sky-50 text-sky-900 border border-sky-200' };
  }
  if (end && today > end) {
    return { label: 'Ended', className: 'bg-amber-50 text-amber-900 border border-amber-200' };
  }
  return { label: 'Live', className: 'bg-emerald-50 text-emerald-900 border border-emerald-200' };
}

function isValidHttpsImageUrl(s: string): boolean {
  return /^https:\/\/.+/i.test(s.trim());
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [fixingProductId, setFixingProductId] = useState<string | null>(null);
  /** Toast shown after auto-copy on new campaign creation. */
  const [toast, setToast] = useState<string | null>(null);
  /** The campaign whose QR code is currently shown. Null = modal closed. */
  const [qrCampaign, setQrCampaign] = useState<CampaignRow | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cRes, mRes] = await Promise.all([
        adminFetch(api('/api/campaigns')),
        adminFetch(api('/api/menu')),
      ]);
      if (!cRes.ok) throw new Error('Failed to load campaigns');
      if (!mRes.ok) throw new Error('Failed to load menu');
      const cJson = await cRes.json();
      const mJson = await mRes.json();
      setCampaigns(cJson.campaigns || []);
      setMenuItems(Array.isArray(mJson.items) ? mJson.items : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const menuById = (id: string) => menuItems.find((m) => m.id === id);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (c: CampaignRow) => {
    setEditingId(c.id);
    setForm({
      title: c.title,
      slug: c.slug,
      is_active: c.is_active,
      starts_at: c.starts_at ? c.starts_at.slice(0, 10) : '',
      ends_at: c.ends_at ? c.ends_at.slice(0, 10) : '',
      show_landing_heading: c.show_landing_heading !== false,
      hero_text: c.hero_text || '',
      thank_you_text: c.thank_you_text || '',
      hero_image_url: c.hero_image_url || '',
      organizer_name: c.organizer_name || '',
      organizer_contact: c.organizer_contact || '',
      menu_item_id: c.menu_item_id,
      max_units: c.max_units != null ? String(c.max_units) : '',
    });
    setShowModal(true);
  };

  useEffect(() => {
    if (form.title && !editingId) {
      setForm((prev) => ({ ...prev, slug: generateSlug(prev.title) }));
    }
  }, [form.title, editingId]);

  const patchCampaign = async (id: string, patch: Record<string, unknown>) => {
    setTogglingId(id);
    setError(null);
    try {
      const res = await adminFetch(api(`/api/campaigns/${id}`), {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Update failed');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setTogglingId(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!form.title.trim() || !form.menu_item_id) {
        throw new Error('Title and product are required.');
      }
      const heroUrl = form.hero_image_url.trim();
      if (heroUrl && !isValidHttpsImageUrl(heroUrl)) {
        throw new Error('Banner image URL must start with https:// (use a direct link to the image file).');
      }
      const productIsVisible = selectedProduct ? selectedProduct.is_available !== false : false;
      const productIsOrderable = selectedProduct ? selectedProduct.is_orderable_online === true : false;
      if (form.is_active && (!productIsVisible || !productIsOrderable)) {
        throw new Error(
          'This campaign is Active, but the selected product is not ready for checkout. Turn on "Show on Menu" and "Online Ordering" for the product first.'
        );
      }
      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        slug: form.slug.trim() || generateSlug(form.title),
        is_active: form.is_active,
        starts_at: form.starts_at.trim() || null,
        ends_at: form.ends_at.trim() || null,
        hero_text: form.hero_text.trim() || null,
        thank_you_text: form.thank_you_text.trim() || null,
        hero_image_url: heroUrl || null,
        show_landing_heading: form.show_landing_heading,
        organizer_name: form.organizer_name.trim() || null,
        organizer_contact: form.organizer_contact.trim() || null,
        menu_item_id: form.menu_item_id,
        max_units: form.max_units.trim() === '' ? null : Math.max(0, parseInt(form.max_units, 10) || 0),
      };
      const url = editingId ? api(`/api/campaigns/${editingId}`) : api('/api/campaigns');
      const res = await adminFetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Save failed');
      setShowModal(false);
      await load();

      // Auto-copy landing link + show toast when creating a new campaign
      if (!editingId) {
        const slug = (payload.slug as string) || generateSlug(form.title);
        const landingUrl = `${origin}/campaign/${slug}`;
        void navigator.clipboard.writeText(landingUrl).catch(() => {});
        setToast(`Campaign created! Landing link copied: ${landingUrl}`);
        setTimeout(() => setToast(null), 5000);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this campaign?')) return;
    const res = await adminFetch(api(`/api/campaigns/${id}`), { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError((data as { error?: string }).error || 'Delete failed');
      return;
    }
    await load();
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // Use toast instead of window.alert so copying doesn't block the page
  const copy = (text: string) => {
    void navigator.clipboard.writeText(text).catch(() => {});
    setToast(`Copied: ${text}`);
    setTimeout(() => setToast(null), 2500);
  };

  const selectedProduct = form.menu_item_id ? menuById(form.menu_item_id) : undefined;
  const datesInvalid =
    !!form.starts_at &&
    !!form.ends_at &&
    new Date(`${form.starts_at}T00:00:00`).getTime() > new Date(`${form.ends_at}T00:00:00`).getTime();
  const productVisible = selectedProduct ? selectedProduct.is_available !== false : false;
  const productOrderable = selectedProduct ? selectedProduct.is_orderable_online === true : false;
  const checkoutReady = !!selectedProduct && productVisible && productOrderable && !datesInvalid;

  const enableProductForCheckout = async () => {
    if (!selectedProduct) return;
    setFixingProductId(selectedProduct.id);
    setError(null);
    try {
      const res = await adminFetch(api(`/api/campaigns/menu-items/${selectedProduct.id}/enable-checkout`), {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as { error?: string }).error || 'Failed to enable product checkout');
      }
      setMenuItems((prev) =>
        prev.map((m) =>
          m.id === selectedProduct.id ? { ...m, is_available: true, is_orderable_online: true } : m
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to enable product checkout');
    } finally {
      setFixingProductId(null);
    }
  };

  /** Compute per-row checkout health from loaded menu items. */
  const rowHealth = (c: CampaignRow): { label: string; className: string } => {
    const item = menuById(c.menu_item_id);
    if (!c.is_active) return { label: '—', className: 'text-gray-400' };
    if (!item) return { label: 'No product', className: 'text-red-600' };
    if (item.is_available === false) return { label: 'Off menu', className: 'text-red-600' };
    if (item.is_orderable_online !== true) return { label: 'No ordering', className: 'text-amber-600' };
    return { label: 'Ready', className: 'text-emerald-600 font-medium' };
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Auto-copy toast — appears 5 s after a new campaign is created */}
      {toast && (
        <div className="fixed top-4 right-4 z-[200] max-w-sm bg-emerald-700 text-white text-sm rounded-xl shadow-xl px-4 py-3 flex items-start gap-3">
          <span className="flex-1">{toast}</span>
          <button type="button" onClick={() => setToast(null)} className="shrink-0 text-white/70 hover:text-white">✕</button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fundraiser campaigns</h1>
          <p className="text-gray-600 text-sm mt-1 max-w-2xl">
            One link for many buyers. Turn a campaign <strong>off</strong> with the switch — links then show "not
            available" (nothing to delete). Optional banner image: paste a public{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">https://</code>{' '}
            link; the product card still uses the photo from <strong>Menu</strong> for that item.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
        >
          <HiPlus className="w-5 h-5" />
          New campaign
        </button>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg p-3">{error}</div>
      )}

      {loading ? (
        <p className="text-gray-600">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Product</th>
                <th className="text-left p-3">Slug</th>
                <th className="text-left p-3">Status</th>
                {/* Health = is product visible + orderable → buy button shows on public page */}
                <th className="text-left p-3">Health</th>
                <th className="text-left p-3">Sold</th>
                {/* Revenue = price × sold_units — quick at-a-glance fundraiser total */}
                <th className="text-right p-3">Revenue</th>
                <th className="text-center p-3 w-28">On</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const st    = campaignRowStatus(c);
                const item  = menuById(c.menu_item_id);
                const pname = item?.name ?? '—';
                // Revenue: only meaningful once at least one unit is sold
                const revenue = item && c.sold_units > 0
                  ? `$${(Number(item.price) * c.sold_units).toFixed(2)}`
                  : '—';
                const health = rowHealth(c);
                return (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="p-3 font-medium text-gray-900">{c.title}</td>
                    <td className="p-3 text-gray-700 max-w-[140px] truncate" title={pname}>
                      {pname}
                    </td>
                    <td className="p-3 text-gray-600 font-mono text-xs">{c.slug}</td>
                    <td className="p-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${st.className}`}>
                        {st.label}
                      </span>
                    </td>
                    {/* Health chip: shows checkout readiness at a glance */}
                    <td className={`p-3 text-xs ${health.className}`} title="Whether the buy button will show on the public fundraiser page">
                      {health.label}
                    </td>
                    <td className="p-3 text-gray-700">
                      {c.sold_units}
                      {c.max_units != null && c.max_units > 0 ? ` / ${c.max_units}` : ''}
                    </td>
                    {/* Revenue cell */}
                    <td className="p-3 text-right font-medium text-gray-800">{revenue}</td>
                    <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer disabled:opacity-40"
                        checked={c.is_active}
                        disabled={togglingId === c.id}
                        title={
                          c.is_active
                            ? 'Turn off: hide landing & checkout for this slug'
                            : 'Turn on: allow public page if dates allow'
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          void patchCampaign(c.id, { is_active: e.target.checked });
                        }}
                      />
                    </td>
                    <td className="p-3 text-right space-x-2 whitespace-nowrap">
                      {/* QR code — opens the QR modal for printing or sharing */}
                      <button
                        type="button"
                        className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                        title="Show QR code for this campaign"
                        onClick={() => setQrCampaign(c)}
                      >
                        <HiQrCode className="w-4 h-4" /> QR
                      </button>
                      <button
                        type="button"
                        className="text-violet-700 hover:underline inline-flex items-center gap-1"
                        onClick={() => copy(`${origin}/campaign/${c.slug}`)}
                      >
                        <HiClipboardCopy className="w-4 h-4" /> Landing
                      </button>
                      <button
                        type="button"
                        className="text-violet-700 hover:underline inline-flex items-center gap-1"
                        onClick={() => copy(`${origin}/order?campaign=${encodeURIComponent(c.slug)}`)}
                      >
                        <HiClipboardCopy className="w-4 h-4" /> Checkout
                      </button>
                      <a
                        href={`/campaign/${c.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gray-600 hover:underline inline-flex items-center gap-1"
                      >
                        <HiExternalLink className="w-4 h-4" /> View
                      </a>
                      <button type="button" className="text-blue-700 hover:underline" onClick={() => openEdit(c)}>
                        <HiPencil className="w-4 h-4 inline" /> Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-700 hover:underline"
                        onClick={() => void remove(c.id)}
                      >
                        <HiTrash className="w-4 h-4 inline" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-gray-500">
                    No campaigns yet. Create one for Mother&apos;s Day, school fundraisers, etc.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit / New campaign modal ───────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5">
            <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit campaign' : 'New campaign'}</h2>
            <div className="space-y-6 text-sm">

              {/* Visibility — how Tammy turns the campaign on or off */}
              <section className="border-b border-gray-100 pb-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Visibility</h3>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                  <div>
                    <span className="font-medium text-gray-900">Campaign is on (Active)</span>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      When this is <strong>off</strong>, the public <strong>Landing</strong> and{' '}
                      <strong>Checkout</strong> links stop working for buyers (they see "not available"). Use
                      this to end early or pause — you do <strong>not</strong> need to delete the campaign.
                      Start/end dates below still apply when Active is on.
                    </p>
                  </div>
                </label>
              </section>

              {/* Readiness checklist */}
              <section className="border-b border-gray-100 pb-4 space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Go-live checklist</h3>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1 text-xs">
                  <p className={selectedProduct ? 'text-green-700' : 'text-amber-700'}>
                    {selectedProduct ? '✓ Product selected' : '• Select a product'}
                  </p>
                  <p className={productVisible ? 'text-green-700' : 'text-amber-700'}>
                    {productVisible ? '✓ Product is visible on menu' : '• Turn on "Show on Menu" for this product'}
                  </p>
                  <p className={productOrderable ? 'text-green-700' : 'text-amber-700'}>
                    {productOrderable ? '✓ Product allows online ordering' : '• Turn on "Online Ordering" for this product'}
                  </p>
                  <p className={datesInvalid ? 'text-red-700' : 'text-green-700'}>
                    {datesInvalid ? '• Date window is invalid (start is after end)' : '✓ Date window is valid'}
                  </p>
                  <p className={checkoutReady ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
                    {checkoutReady ? 'Ready: landing page will show the purchase button.' : 'Not ready: checkout button will be hidden on public page.'}
                  </p>
                </div>
              </section>

              {/* Basics: title, slug, product, cap */}
              <section className="border-b border-gray-100 pb-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Basics</h3>
                <label className="block">
                  <span className="font-medium text-gray-700">Title</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="font-medium text-gray-700">URL slug</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="mothers-day-2026"
                  />
                </label>
                <label className="block">
                  <span className="font-medium text-gray-700">Product (menu item)</span>
                  <select
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.menu_item_id}
                    onChange={(e) => setForm((f) => ({ ...f, menu_item_id: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    {menuItems.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} — ${Number(m.price).toFixed(2)} ({m.category})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Avoid sandwiches, drinks, soups, stuffed cookies, and butter tarts — those need extra choices on the menu page.
                  </p>
                  {selectedProduct && selectedProduct.is_available === false ? (
                    <p className="text-xs text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mt-2">
                      This product is <strong>hidden / off the menu</strong> right now. Buyers will see your
                      fundraiser text but <strong>cannot check out</strong> until you turn the item back on
                      under <strong>Menu</strong> → edit item → Available.
                    </p>
                  ) : null}
                  {selectedProduct && selectedProduct.is_orderable_online !== true ? (
                    <p className="text-xs text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mt-2">
                      This product has <strong>Online Ordering OFF</strong>, so fundraiser buyers will see "not available"
                      and cannot purchase until it is enabled.
                    </p>
                  ) : null}
                  {selectedProduct && (!productVisible || !productOrderable) ? (
                    <button
                      type="button"
                      className="mt-2 text-xs px-3 py-2 rounded-lg border border-green-200 bg-green-50 text-green-800 hover:bg-green-100 disabled:opacity-50"
                      onClick={() => void enableProductForCheckout()}
                      disabled={fixingProductId === selectedProduct.id}
                    >
                      {fixingProductId === selectedProduct.id
                        ? 'Enabling checkout...'
                        : 'One-click fix: enable Show on Menu + Online Ordering'}
                    </button>
                  ) : null}
                  {selectedProduct?.image_url ? (
                    <p className="text-xs text-gray-600 mt-2 flex items-center gap-2">
                      <img src={selectedProduct.image_url} alt="" className="w-12 h-12 rounded object-cover border" />
                      <span>
                        This product&apos;s photo from <strong>Menu</strong> appears on the fundraiser card. To
                        change it, edit the item in <strong>Menu</strong>.
                      </span>
                    </p>
                  ) : selectedProduct ? (
                    <p className="text-xs text-amber-800 mt-2">
                      This menu item has no photo yet — add one under <strong>Menu</strong> so the campaign page looks good.
                    </p>
                  ) : null}
                </label>
                <label className="block">
                  <span className="font-medium text-gray-700">Max units (optional)</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.max_units}
                    onChange={(e) => setForm((f) => ({ ...f, max_units: e.target.value }))}
                    placeholder="Leave blank for no cap"
                  />
                </label>
              </section>

              {/* Schedule */}
              <section className="border-b border-gray-100 pb-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Schedule (optional)
                </h3>
                <p className="text-xs text-gray-600">
                  Leave both blank for "always" while Active is on. With dates, buyers only see the page between
                  start and end (local calendar days).
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-medium text-gray-700">Start date</span>
                    <input
                      type="date"
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                      value={form.starts_at}
                      onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                    />
                  </label>
                  <label className="block">
                    <span className="font-medium text-gray-700">End date</span>
                    <input
                      type="date"
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                      value={form.ends_at}
                      onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                    />
                  </label>
                </div>
              </section>

              {/* Sharing tools — links, email, UTM-tracked presets */}
              <section className="border-b border-gray-100 pb-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Share tools</h3>
                {form.slug.trim() ? (() => {
                  const slug = form.slug.trim();
                  const landingUrl = `${origin}/campaign/${slug}`;
                  const checkoutUrl = `${origin}/order?campaign=${encodeURIComponent(slug)}`;
                  const title = form.title.trim() || 'Our fundraiser';
                  const emailSubject = encodeURIComponent(`Support ${title} — order from Nanny B's Bakery`);
                  const emailBody = encodeURIComponent(
                    `Hi!\n\nPlease support ${title}. Order your treats here:\n${landingUrl}\n\nThank you!`
                  );
                  // UTM variants let Tammy see where orders came from in analytics
                  const utmLink = (source: string, medium: string) =>
                    `${landingUrl}?utm_source=${source}&utm_medium=${medium}&utm_campaign=${encodeURIComponent(slug)}`;
                  return (
                    <>
                      {/* Basic copy/open actions */}
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Quick actions</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded border border-gray-200 bg-white hover:bg-gray-50"
                            onClick={() => copy(landingUrl)}
                          >
                            <HiClipboardCopy className="w-3.5 h-3.5" /> Copy landing link
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded border border-gray-200 bg-white hover:bg-gray-50"
                            onClick={() => copy(checkoutUrl)}
                          >
                            <HiClipboardCopy className="w-3.5 h-3.5" /> Copy checkout link
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded border border-gray-200 bg-white hover:bg-gray-50"
                            onClick={() => copy(`Support ${title}! Order here: ${landingUrl}`)}
                          >
                            <HiClipboardCopy className="w-3.5 h-3.5" /> Copy social blurb
                          </button>
                          <a
                            href={landingUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded border border-gray-200 bg-white hover:bg-gray-50"
                          >
                            <HiExternalLink className="w-3.5 h-3.5" /> Open landing
                          </a>
                          {/* Send by email — opens default mail app with subject + body prefilled */}
                          <a
                            href={`mailto:?subject=${emailSubject}&body=${emailBody}`}
                            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded border border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100"
                          >
                            <HiMail className="w-3.5 h-3.5" /> Send by email
                          </a>
                        </div>
                      </div>

                      {/* UTM-tracked links — separate per source so Tammy can see where buyers came from */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          <HiLink className="w-3.5 h-3.5" />
                          Tracked links (copies a unique link per source — same page, better reporting)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: 'School newsletter', source: 'school-newsletter', medium: 'email' },
                            { label: 'Facebook', source: 'facebook', medium: 'social' },
                            { label: 'Instagram', source: 'instagram', medium: 'social' },
                            { label: 'Partner website', source: 'partner', medium: 'referral' },
                          ].map(({ label, source, medium }) => (
                            <button
                              key={source}
                              type="button"
                              className="text-xs px-3 py-2 rounded border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
                              onClick={() => copy(utmLink(source, medium))}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  );
                })() : (
                  <p className="text-xs text-gray-500">Enter a title/slug to generate share tools.</p>
                )}
              </section>

              {/* Organizer — who is running this fundraiser and how to reach them */}
              <section className="border-b border-gray-100 pb-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Organizer (optional)
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Shown at the bottom of the public page so buyers know who is running the fundraiser and how to
                  reach them with questions. Leave blank if you are handling all contact yourself.
                </p>
                <label className="block">
                  <span className="font-medium text-gray-700">Organizer name</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.organizer_name}
                    onChange={(e) => setForm((f) => ({ ...f, organizer_name: e.target.value }))}
                    placeholder="Lincoln School PTO"
                  />
                </label>
                <label className="block">
                  <span className="font-medium text-gray-700">Contact info</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.organizer_contact}
                    onChange={(e) => setForm((f) => ({ ...f, organizer_contact: e.target.value }))}
                    placeholder="Jane Smith · 705-555-1234 · jane@school.ca"
                  />
                </label>
              </section>

              {/* Banner image */}
              <section className="border-b border-gray-100 pb-4 space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Banner image (optional)
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Upload a file from your device, browse existing images, or paste a direct
                  <strong> https://</strong> URL. This shows wide above the title — no homepage editor needed.
                </p>
                <ImageSelector
                  value={form.hero_image_url}
                  onChange={(url) => setForm((f) => ({ ...f, hero_image_url: url }))}
                  defaultTab="upload"
                  previewAspect="wide"
                  previewHeight="h-40"
                  placeholder="https://example.com/my-fundraiser-banner.jpg"
                  helpText="Tip: Upload is easiest. URL still works if you already have a direct https image link."
                />
              </section>

              {/* Words on the page */}
              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Words on the page
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Everything here is optional. If your banner image is a full poster with all the wording, leave
                  the text boxes blank and turn off the headline below — buyers still see the product card and
                  checkout button.
                </p>
                <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    checked={form.show_landing_heading}
                    onChange={(e) => setForm((f) => ({ ...f, show_landing_heading: e.target.checked }))}
                  />
                  <div>
                    <span className="font-medium text-gray-900">
                      Show "Fundraiser" line and big title on the public page
                    </span>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Uncheck when your poster image already includes the fundraiser name and you do not want a
                      second headline. The title above is still saved for your admin list and for the browser tab
                      (SEO).
                    </p>
                  </div>
                </label>
                <label className="block">
                  <span className="font-medium text-gray-700">Hero text</span>
                  <p className="text-xs text-gray-500 mt-0.5 mb-1">
                    Shows under the big title on the public landing page (when the headline is on). A few
                    sentences: who benefits, deadline, pickup reminder. Optional.
                  </p>
                  <textarea
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    rows={4}
                    value={form.hero_text}
                    onChange={(e) => setForm((f) => ({ ...f, hero_text: e.target.value }))}
                    placeholder={`Example: Every sheet cake helps Lincoln School…\nPickup at Nanny B's in Barrie — choose your date at checkout.`}
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded border border-violet-200 bg-violet-50 text-violet-900 hover:bg-violet-100"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          hero_text: COPY_SNIPPETS.schoolFundraiser.hero,
                          thank_you_text: f.thank_you_text.trim()
                            ? f.thank_you_text
                            : COPY_SNIPPETS.schoolFundraiser.thankYou,
                        }))
                      }
                    >
                      Insert school fundraiser (hero + thank-you)
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded border border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          hero_text: COPY_SNIPPETS.short.hero,
                          thank_you_text: f.thank_you_text.trim()
                            ? f.thank_you_text
                            : COPY_SNIPPETS.short.thankYou,
                        }))
                      }
                    >
                      Insert short &amp; simple (hero + thank-you)
                    </button>
                  </div>
                </label>
                <label className="block">
                  <span className="font-medium text-gray-700">Thank-you / partner message</span>
                  <p className="text-xs text-gray-500 mt-0.5 mb-1">
                    Shows in a highlighted box above the product on the landing page. Good for pickup notes or a
                    thank-you to the partner. Optional.
                  </p>
                  <textarea
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    rows={4}
                    value={form.thank_you_text}
                    onChange={(e) => setForm((f) => ({ ...f, thank_you_text: e.target.value }))}
                    placeholder="Example: Thank you PTO! Questions: Jane at… Pickup at the bakery."
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded border border-violet-200 bg-violet-50 text-violet-900 hover:bg-violet-100"
                      onClick={() =>
                        setForm((f) => ({ ...f, thank_you_text: COPY_SNIPPETS.schoolFundraiser.thankYou }))
                      }
                    >
                      Insert thank-you (school style)
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded border border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100"
                      onClick={() =>
                        setForm((f) => ({ ...f, thank_you_text: COPY_SNIPPETS.short.thankYou }))
                      }
                    >
                      Insert thank-you (short)
                    </button>
                  </div>
                </label>
              </section>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
              <button
                type="button"
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR code modal ─────────────────────────────────────────────────── */}
      {qrCampaign && (() => {
        const campaignUrl = `${origin}/campaign/${qrCampaign.slug}`;
        // Free, no-login QR image API — returns a PNG at the given size
        const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&margin=12&format=png&data=${encodeURIComponent(campaignUrl)}`;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
            onClick={() => setQrCampaign(null)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-80 p-6 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-base font-semibold text-gray-900 mb-1 truncate">{qrCampaign.title}</h2>
              <p className="text-xs text-gray-500 mb-4 truncate">{campaignUrl}</p>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 inline-block mb-4">
                <img src={qrSrc} alt={`QR code for ${qrCampaign.title}`} width={200} height={200} />
              </div>
              <div className="flex flex-col gap-2">
                {/* Download — open the QR image in a new tab so the user can right-click save / print */}
                <a
                  href={qrSrc}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-2 px-4 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 text-center"
                >
                  Open full-size (right-click to save)
                </a>
                <button
                  type="button"
                  className="w-full py-2 px-4 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setQrCampaign(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
